/* Live BKR + certificaten-check panel — extracted from RoosterPage. */
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Coverage } from "./ChipsCoverage";

export default function ShiftLiveCheck({ dryRun, busy, form }) {
  const hasForm = form.medewerker_id && form.group_id && form.start && form.eind;
  if (!hasForm) {
    return (
      <div className="p-4 rounded-md border border-dashed border-neutral-300 bg-neutral-50 text-sm text-neutral-500 text-center"
           data-testid="live-check-empty">
        Kies medewerker, groep en tijden om de live BKR-check te zien.
      </div>
    );
  }
  if (dryRun?.error) {
    return (
      <div className="p-3 rounded-md border border-red-200 bg-red-50 text-xs text-red-800"
           data-testid="live-check-error">
        {dryRun.error}
      </div>
    );
  }
  const scope = dryRun?.scope;
  const med = dryRun?.medewerker;

  const bkrTone = !scope ? "bg-neutral-100 text-neutral-500"
    : scope.bkr_ok
      ? (scope.after.staff_count > scope.required + 1 && scope.required > 0
        ? "bg-blue-50 border-blue-200 text-blue-800"
        : "bg-emerald-50 border-emerald-200 text-emerald-800")
      : "bg-red-50 border-red-200 text-red-800";

  return (
    <div className="rounded-md border border-neutral-200 bg-white p-3 space-y-3" data-testid="live-check">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-neutral-800">Live BKR & certificaten</div>
        {busy && (
          <span className="text-[10px] text-neutral-500 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> berekenen…
          </span>
        )}
      </div>

      <div className={`rounded-md border p-3 ${bkrTone}`} data-testid="live-check-bkr">
        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">BKR — {scope?.kind || "?"}</div>
        <div className="flex items-baseline gap-3">
          <div className="text-2xl font-bold tabular-nums">
            {scope ? scope.after.staff_count : "—"}
            <span className="text-neutral-400 text-sm font-normal">/{scope ? Math.max(scope.required, 1) : "?"}</span>
          </div>
          <div className="text-xs">
            {scope?.kinderen ?? 0} kinderen aanwezig
            {scope?.baby_aanwezig ? " · incl. baby's <12mnd" : ""}
          </div>
        </div>
        {scope && (
          <div className="text-[11px] mt-1 opacity-90">
            {scope.bkr_ok
              ? (scope.after.staff_count > scope.required + 1 && scope.required > 0
                ? `Overbezet (${scope.after.staff_count - scope.required} boven vereist)`
                : "Bezetting OK")
              : `Onderbezet — nog ${scope.required - scope.after.staff_count} medewerker(s) nodig`}
            {scope.before && (
              <span className="text-neutral-500 ml-1">(was {scope.before.staff_count})</span>
            )}
          </div>
        )}
      </div>

      {scope && (
        <div className="grid grid-cols-2 gap-2" data-testid="live-check-coverage">
          <Coverage label="Diploma PM" ok={scope.after.has_diploma} was={scope.before?.has_diploma} />
          <Coverage label="Opener" ok={scope.after.has_opener} was={scope.before?.has_opener} />
          <Coverage label="Sluiter" ok={scope.after.has_sluiter} was={scope.before?.has_sluiter} />
          <Coverage label="EHBO" ok={scope.after.has_ehbo} was={scope.before?.has_ehbo} advisory />
          <Coverage label="Babytraining" ok={scope.after.has_babytraining} was={scope.before?.has_babytraining} required={scope.baby_aanwezig} />
        </div>
      )}

      {med && (
        <div className="pt-2 border-t border-neutral-100 text-xs" data-testid="live-check-med">
          <div className="text-neutral-500 mb-1 text-[10px] uppercase tracking-widest">Gekozen medewerker</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-neutral-800">{med.naam}</span>
            {med.gediplomeerd
              ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Diploma PM</Badge>
              : <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">GEEN diploma</Badge>}
            {med.heeft_ehbo && <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]">EHBO</Badge>}
            {med.heeft_babytraining && <Badge className="bg-pink-50 text-pink-700 border-pink-200 text-[10px]">Babytraining</Badge>}
            <span className="text-[10px] text-neutral-500 ml-auto">
              week-uren na deze shift: <strong className="text-neutral-800">{dryRun?.week_uren}</strong>u
            </span>
          </div>
        </div>
      )}

      {(dryRun?.warnings?.length ?? 0) > 0 && (
        <div className="pt-2 border-t border-neutral-100 space-y-1" data-testid="live-check-warnings">
          {dryRun.warnings.map((w, i) => (
            <div key={`${w.level}-${w.message || i}-${i}`} className={
              "text-[11px] px-2 py-1 rounded flex items-start gap-1.5 " +
              (w.level === "alert"
                ? "bg-red-50 text-red-800 border border-red-200"
                : w.level === "warning"
                  ? "bg-amber-50 text-amber-800 border border-amber-200"
                  : "bg-neutral-50 text-neutral-700 border border-neutral-200")
            }>
              {w.level === "alert"
                ? <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                : <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" />}
              <span>{w.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
